
import React, { createContext, useEffect, useState } from 'react';
import { googleAuthService } from './google';
import { useAuthQueries } from './useAuth';
import { logSecurityEvent } from '@/utils/security';
import type { User, AuthError, LoginResponse } from './types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
  login: (credential: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<LoginResponse | null>;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  loginError: AuthError | null;
  logoutError: AuthError | null;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  const authQueries = useAuthQueries();

  // Initialize Google Auth with enhanced error handling
  useEffect(() => {
    const initializeGoogle = async () => {
      try {
        logSecurityEvent('google_auth_init_start');
        await googleAuthService.initialize();
        setIsGoogleLoaded(true);
        logSecurityEvent('google_auth_init_success');
      } catch (error) {
        console.error('Failed to initialize Google Auth:', error);
        logSecurityEvent('google_auth_init_error', { 
          error: error instanceof Error ? error.message : 'Unknown' 
        });
      }
    };

    initializeGoogle();
  }, []);

  // Set up Google callback when both Google is loaded and we have the auth functions
  useEffect(() => {
    if (!isGoogleLoaded) return;

    try {
      googleAuthService.initializeGoogleAuth(async (response) => {
        try {
          logSecurityEvent('google_callback_received');
          await authQueries.login(response.credential);
        } catch (error) {
          console.error('Google login callback failed:', error);
          logSecurityEvent('google_callback_error', { 
            error: error instanceof Error ? error.message : 'Unknown' 
          });
        }
      });
    } catch (error) {
      console.error('Failed to setup Google auth callback:', error);
      logSecurityEvent('google_callback_setup_error', { 
        error: error instanceof Error ? error.message : 'Unknown' 
      });
    }
  }, [isGoogleLoaded, authQueries.login]);

  // Enhanced session monitoring
  useEffect(() => {
    if (!authQueries.isAuthenticated) return;

    // Set up periodic session validation (every 15 minutes)
    const sessionCheckInterval = setInterval(async () => {
      try {
        await authQueries.refreshSession();
      } catch (error) {
        logSecurityEvent('session_check_error', { 
          error: error instanceof Error ? error.message : 'Unknown' 
        });
      }
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(sessionCheckInterval);
  }, [authQueries.isAuthenticated, authQueries.refreshSession]);

  const contextValue: AuthContextType = {
    ...authQueries,
    isLoading: authQueries.isLoading || !isGoogleLoaded,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
